import { getRumClientForIntegrations } from '@mint-lib/datadog';
import type { IFlagsmith } from '@mint-lib/flags';
import { FlagsmithProviders } from '@mint-lib/flags-envs';
import type {
  DefaultError,
  FetchQueryOptions,
  QueryKey,
} from '@mint-lib/query-client';
import { i18nFactory, requestFactory } from '@mint-lib/routing-context';
import {
  ServiceLocator,
  type ServiceArgs,
  type ServiceInstance,
  type ServicesNames,
} from '@mint-lib/service-locator';

import { authFactory } from './factories/auth.js';
import { flagsmithEventsAbstractFactory } from './factories/flagsmith-events.js';
import { flagsmithAbstractFactory } from './factories/flagsmith.js';
import { httpFactory } from './factories/http.js';
import { queryClientFactory } from './factories/queryClient.js';
import type { MintAdminServices, RouterContext } from './types.js';

export function makeRoutingContext(): RouterContext {
  const serviceLocator = new ServiceLocator<MintAdminServices>(console);
  // @ts-ignore TODO: Fix type when proper login will be added
  serviceLocator.registerAbstractFactory('auth', authFactory);
  serviceLocator.registerInstance('datadog', getRumClientForIntegrations());
  serviceLocator.registerAbstractFactory(
    'flagsmithEvents',
    flagsmithEventsAbstractFactory,
  );
  serviceLocator.registerAbstractFactory('flagsmith', flagsmithAbstractFactory);
  // @ts-expect-error TS2345. This factory can be used with wrong types
  serviceLocator.registerAbstractFactory('i18n', i18nFactory);
  serviceLocator.registerAbstractFactory('http', httpFactory);
  serviceLocator.registerAbstractFactory('queryClient', queryClientFactory);
  // @ts-expect-error TS2345. This factory can be used with wrong types
  serviceLocator.registerAbstractFactory('request', requestFactory);

  async function isFlagEnabled(
    env: keyof typeof FlagsmithProviders,
    flagName: string,
  ) {
    return (
      await serviceLocator.getOrThrowInstance('flagsmith', env)
    ).hasFeature(flagName);
  }

  async function isAuthenticated() {
    return (await serviceLocator.getOrThrowInstance('auth')).isLoggedIn();
  }

  async function getAuth0Credentials() {
    return (await serviceLocator.getOrThrowInstance('auth')).auth0Credentials;
  }

  async function getGuardContext(flagsService?: IFlagsmith) {
    return {
      authService: await serviceLocator.getOrThrowInstance('auth'),
      flagsService:
        flagsService ||
        (await serviceLocator.getOrThrowInstance('flagsmith', '@mint/app')),
    };
  }

  async function prefetchQuery<
    TQueryFnData = unknown,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
    TPageParam = never,
  >(
    queryOptions: FetchQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
  ) {
    const queryClient = await serviceLocator.getOrThrowInstance('queryClient');

    return queryClient.fetchQuery(queryOptions);
  }

  async function load<T>(
    url: string,
    params?: Record<string, string | number>,
    type: 'guestApi' | 'defaultApi' = 'defaultApi',
  ): Promise<T> {
    return serviceLocator.getOrThrowInstance('request', url, params, type) as T;
  }

  async function getInstance<
    Key extends ServicesNames<MintAdminServices>,
    Args extends ServiceArgs<MintAdminServices, Key>,
    Instance extends ServiceInstance<MintAdminServices, Key, Args>,
  >(name: Key, ...args: Args): Promise<Instance> {
    return serviceLocator.getOrThrowInstance<Key, Args, Instance>(
      name,
      ...args,
    );
  }

  return {
    getInstance,
    isFlagEnabled,
    isAuthenticated,
    getGuardContext,
    load,
    serviceLocator,
    getAuth0Credentials,
    prefetchQuery,
  };
}
